import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSettings } from '../../../contexts/SettingsContext';
import { useDashboardLayout, useDashboardLayouts, useUpdateDashboardLayout, useSetDefaultLayout, useDeleteDashboardLayout, type WidgetConfig, type DashboardGroup } from '../hooks/useDashboardLayout';
import { getAllWidgetDefinitions, getWidgetDefinition, type WidgetCategory } from '../widgetRegistry';
import WidgetConfigPanel from './WidgetConfigPanel';
import { Button, Label, TextInput, Select, Card, Badge, ConfirmationDialog } from '../../../components/ui';
import { XIcon } from '../../../components/ui/Icons';

export interface DashboardSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  layoutName?: string;
  editingWidgetId: string | null;
  onClearEditingWidget: () => void;
}

const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  isOpen,
  onClose,
  layoutName,
  editingWidgetId,
  onClearEditingWidget,
}) => {
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const dashboardGrid = appConfig.dashboard;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedLayoutName, setSelectedLayoutName] = useState<string | undefined>(undefined);
  const effectiveLayoutName = selectedLayoutName ?? layoutName;
  const { layout, mutate: mutateCurrentLayout } = useDashboardLayout(effectiveLayoutName);
  const { layouts, mutate: mutateLayouts } = useDashboardLayouts();

  useEffect(() => {
    if (isOpen) setSelectedLayoutName(layoutName ?? undefined);
  }, [isOpen, layoutName]);

  // When user selects a different dashboard in the listbox, reload from backend (DashboardLayout)
  useEffect(() => {
    if (effectiveLayoutName) mutateCurrentLayout();
  }, [effectiveLayoutName, mutateCurrentLayout]);

  const updateLayoutBase = useUpdateDashboardLayout();
  const updateLayout = useCallback(
    async (layoutId: number, payload: { name?: string; isDefault?: boolean; widgets?: WidgetConfig[]; groups?: DashboardGroup[] }) => {
      await updateLayoutBase(layoutId, payload);
      mutateLayouts();
      mutateCurrentLayout();
    },
    [updateLayoutBase, mutateLayouts, mutateCurrentLayout]
  );
  const setDefaultLayout = useSetDefaultLayout();
  const deleteLayout = useDeleteDashboardLayout();
  const [configPanelWidget, setConfigPanelWidget] = useState<{ id: string; type: string; settings: Record<string, unknown> } | null>(null);
  const [editingLayoutId, setEditingLayoutId] = useState<number | null>(null);
  const [editingLayoutName, setEditingLayoutName] = useState('');

  const defaultWidthByCategory = useMemo(
    () => ({
      stats: dashboardGrid.statCardWidth,
      lists: dashboardGrid.listWidgetWidth,
      charts: dashboardGrid.chartWidth,
      analytics: dashboardGrid.chartWidth,
      calendar: dashboardGrid.listWidgetWidth,
      interactive: dashboardGrid.listWidgetWidth,
    }),
    [dashboardGrid]
  );

  const layoutWidgets = layout?.widgets;
  const widgets: WidgetConfig[] = Array.isArray(layoutWidgets) ? layoutWidgets : [];
  const sortedWidgets = useMemo(
    () => [...widgets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [widgets]
  );
  const groups: DashboardGroup[] = layout && Array.isArray(layout.groups) ? layout.groups : [];
  const availableDefinitions = getAllWidgetDefinitions();
  const usedWidgetTypes = useMemo(() => new Set(widgets.map((w) => w.widgetType)), [widgets]);
  const availableToAdd = useMemo(
    () => availableDefinitions.filter((d) => !usedWidgetTypes.has(d.id)),
    [availableDefinitions, usedWidgetTypes]
  );

  const categoryLabels: Record<WidgetCategory | '', string> = {
    '': 'All',
    stats: 'Stats',
    lists: 'Lists',
    charts: 'Charts',
    analytics: 'Analytics',
    calendar: 'Calendar',
    interactive: 'Interactive',
  };
  const categoriesInAvailable = useMemo(
    () => Array.from(new Set(availableToAdd.map((d) => d.category))).sort(),
    [availableToAdd]
  );
  const [addWidgetCategory, setAddWidgetCategory] = useState<WidgetCategory | ''>('');
  const availableToAddFiltered = useMemo(
    () => (addWidgetCategory ? availableToAdd.filter((d) => d.category === addWidgetCategory) : availableToAdd),
    [availableToAdd, addWidgetCategory]
  );

  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupTitle, setEditingGroupTitle] = useState('');
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ layoutId: number; name: string } | null>(null);

  const handleReorderWidgets = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const withoutDragged = sortedWidgets.filter((w) => w.id !== draggedId);
      const targetIndex = withoutDragged.findIndex((w) => w.id === targetId);
      const dragged = widgets.find((w) => w.id === draggedId);
      if (!dragged) return;
      const insertAt = targetIndex >= 0 ? targetIndex : withoutDragged.length;
      const reordered = [...withoutDragged.slice(0, insertAt), dragged, ...withoutDragged.slice(insertAt)];
      const updated = reordered.map((w, i) => ({ ...w, order: i }));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, sortedWidgets, widgets, updateLayout, mutateLayouts]
  );

  const handleAddWidget = useCallback(
    (widgetType: string) => {
      const def = getWidgetDefinition(widgetType);
      if (!def || !layout?.id) return;
      const id = `w-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const w = defaultWidthByCategory[def.category] ?? def.defaultSize.w;
      const h = def.defaultSize.h;
      const newWidget: WidgetConfig = {
        id,
        widgetType,
        position: { x: 0, y: widgets.length + 10 },
        size: { w, h },
        settings: { ...def.defaultSettings },
        order: widgets.length,
      };
      const updated = [...widgets, newWidget];
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, defaultWidthByCategory]
  );

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      if (!layout?.id) return;
      const updated = widgets.filter((w) => w.id !== widgetId);
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      if (editingWidgetId === widgetId) onClearEditingWidget();
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, editingWidgetId, onClearEditingWidget]
  );

  const handleOpenConfig = useCallback((w: WidgetConfig) => {
    setConfigPanelWidget({ id: w.id, type: w.widgetType, settings: w.settings ?? {} });
  }, []);

  const handleSaveWidgetSettings = useCallback(
    (settings: Record<string, unknown>) => {
      if (!configPanelWidget || !layout?.id) return;
      const updated = widgets.map((w) =>
        w.id === configPanelWidget.id ? { ...w, settings } : w
      );
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      setConfigPanelWidget(null);
    },
    [configPanelWidget, layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleAddGroup = useCallback(() => {
    if (!layout?.id || !newGroupTitle.trim()) return;
    const id = `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const next: DashboardGroup[] = [...groups, { id, title: newGroupTitle.trim() }];
    updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
    setNewGroupTitle('');
  }, [layout?.id, groups, newGroupTitle, updateLayout, mutateLayouts]);

  const handleRenameGroup = useCallback(
    (groupId: string, title: string) => {
      if (!layout?.id || !title.trim()) return;
      const next = groups.map((g) => (g.id === groupId ? { ...g, title: title.trim() } : g));
      updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
      setEditingGroupTitle('');
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      if (!layout?.id) return;
      const nextGroups = groups.filter((g) => g.id !== groupId);
      const updatedWidgets = widgets.map((w) => (w.groupId === groupId ? { ...w, groupId: undefined } : w));
      updateLayout(layout.id, { groups: nextGroups, widgets: updatedWidgets }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
    },
    [layout?.id, groups, widgets, updateLayout, mutateLayouts]
  );

  const handleSetWidgetGroup = useCallback(
    (widgetId: string, groupId: string | undefined) => {
      if (!layout?.id) return;
      const updated = widgets.map((w) => (w.id === widgetId ? { ...w, groupId: groupId || undefined } : w));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleReorderGroups = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const newOrder = [...groups];
      const from = newOrder.findIndex((x) => x.id === draggedId);
      const to = newOrder.findIndex((x) => x.id === targetId);
      if (from === -1 || to === -1) return;
      const [removed] = newOrder.splice(from, 1);
      newOrder.splice(to, 0, removed);
      updateLayout(layout.id, { groups: newOrder }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleSetDefault = useCallback(
    (layoutId: number) => {
      setDefaultLayout(layoutId).then(() => mutateLayouts()).catch(() => {});
      setEditingLayoutId(null);
    },
    [setDefaultLayout, mutateLayouts]
  );

  const handleRenameSubmit = useCallback(
    (layoutId: number) => {
      const trimmed = editingLayoutName.trim();
      if (!trimmed) return;
      updateLayout(layoutId, { name: trimmed }).then(() => {
        mutateLayouts();
        setEditingLayoutId(null);
        setEditingLayoutName('');
      }).catch(() => {});
    },
    [editingLayoutName, updateLayout, mutateLayouts]
  );

  const handleDeleteClick = useCallback((layoutId: number, name: string) => {
    setDeleteConfirm({ layoutId, name });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const payload = deleteConfirm;
    if (!payload) return;
    setDeleteConfirm(null);
    const { layoutId, name: _name } = payload;
    const isCurrent = layout?.id === layoutId || (layoutName && layouts.find((l) => l.id === layoutId)?.name === layoutName);
    deleteLayout(layoutId).then(() => {
      mutateLayouts();
      if (isCurrent) {
        const next = new URLSearchParams(searchParams);
        next.delete('dashboard');
        navigate({ pathname: '/dashboard', search: next.toString() });
      }
      if (editingLayoutId === layoutId) setEditingLayoutId(null);
    }).catch(() => {});
  }, [deleteConfirm, layout?.id, layoutName, layouts, deleteLayout, mutateLayouts, navigate, searchParams, editingLayoutId]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center sm:p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
        <div
          className={`relative z-50 w-full sm:max-w-6xl max-h-[92dvh] sm:max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 pb-[env(safe-area-inset-bottom)] sm:pb-0`}
          style={{ minHeight: 'min(400px, 92dvh)' }}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
                <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Dashboard settings</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Editing: <span className="font-medium text-slate-700 dark:text-slate-300">{layout?.name ?? 'Default'}</span>
                  {' · '}Widgets, groups and layout are specific to this dashboard
                </p>
              </div>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <XIcon className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {configPanelWidget ? (
              <div className="p-4 sm:p-6">
                <WidgetConfigPanel
                  widgetId={configPanelWidget.id}
                  widgetType={configPanelWidget.type}
                  settings={configPanelWidget.settings}
                  onSave={handleSaveWidgetSettings}
                  onClose={() => setConfigPanelWidget(null)}
                />
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-6">
                {/* Select dashboard to edit - native select so dropdown is not clipped by overflow */}
                <Card className="rounded-2xl overflow-visible">
                  <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Dashboard to edit</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Choose which dashboard&apos;s widgets and groups to configure below</p>
                  </div>
                  <div className="p-4 sm:p-5">
                    <Label htmlFor="dashboard-edit-listbox">Selected dashboard</Label>
                    <select
                      id="dashboard-edit-listbox"
                      value={effectiveLayoutName ?? layout?.name ?? layouts[0]?.name ?? ''}
                      onChange={(e) => setSelectedLayoutName(e.target.value || undefined)}
                      className="mt-2 w-full min-w-0 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      aria-label="Select dashboard to edit"
                    >
                      {layouts.length === 0 && layout ? (
                        <option value={layout.name}>{layout.name}{layout.isDefault ? ' (default)' : ''}</option>
                      ) : (
                        layouts.map((l) => (
                          <option key={l.id} value={l.name}>
                            {l.name}{l.isDefault ? ' (default)' : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </Card>

                {/* My dashboards */}
                <Card className="overflow-hidden rounded-2xl">
                  <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">My dashboards</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Switch, set default, rename or delete saved dashboards</p>
                  </div>
                  <div className="p-4 sm:p-5">
                    {layouts.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No saved dashboards yet. Use &quot;Save as new dashboard&quot; in the dashboard bar to create one.</p>
                    ) : (
                      <ul className="space-y-2">
                        {layouts.map((l) => (
                          <li
                            key={l.id}
                            className="flex flex-col gap-2 sm:flex-row sm:items-center justify-between py-2.5 px-3 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800/50"
                          >
                            {editingLayoutId === l.id ? (
                              <>
                                <TextInput
                                  value={editingLayoutName}
                                  onChange={(e) => setEditingLayoutName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit(l.id)}
                                  className="flex-1 min-w-0"
                                  showClearButton={false}
                                />
                                <div className="flex gap-1.5">
                                  <Button type="button" size="icon" onClick={() => handleRenameSubmit(l.id)} title="Save" aria-label="Save">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  </Button>
                                  <Button type="button" variant="outline" size="icon" onClick={() => { setEditingLayoutId(null); setEditingLayoutName(''); }} title="Cancel" aria-label="Cancel">
                                    <XIcon className="w-5 h-5" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-gray-900 dark:text-white truncate">{l.name}</span>
                                  {l.isDefault && <Badge variant="accent">Default</Badge>}
                                </div>
                                <div className="flex items-center gap-1">
                                  {!l.isDefault && (
                                    <Button type="button" variant="ghost" size="sm" onClick={() => handleSetDefault(l.id)} title="Set as default">
                                      Set default
                                    </Button>
                                  )}
                                  <Button type="button" variant="ghost" size="icon" onClick={() => { setEditingLayoutId(l.id); setEditingLayoutName(l.name); }} title="Rename" aria-label="Rename">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteClick(l.id, l.name)} title="Delete this dashboard" aria-label="Delete dashboard" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </Button>
                                </div>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>

                {/* Groups card */}
                <Card className="overflow-hidden rounded-2xl">
                  <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Groups</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Create sections for this dashboard (e.g. Today Agenda). Drag to reorder.</p>
                  </div>
                  <div className="p-4 sm:p-5 space-y-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <TextInput
                        value={newGroupTitle}
                        onChange={(e) => setNewGroupTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
                        placeholder="e.g. Today Agenda, Key metrics"
                        className="flex-1 min-w-0"
                        showClearButton={false}
                      />
                      <Button type="button" onClick={handleAddGroup} disabled={!newGroupTitle.trim()}>
                        Add group
                      </Button>
                    </div>
                    {groups.length > 0 && (
                      <ul className="space-y-2">
                        {groups.map((g) => (
                          <li
                            key={g.id}
                            draggable={editingGroupId !== g.id}
                            onDragStart={(e) => {
                              if (editingGroupId === g.id) return;
                              e.dataTransfer.setData('text/plain', g.id);
                              e.dataTransfer.effectAllowed = 'move';
                              setDraggedGroupId(g.id);
                            }}
                            onDragEnd={() => setDraggedGroupId(null)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const draggedId = e.dataTransfer.getData('text/plain');
                              if (draggedId && draggedId !== g.id) handleReorderGroups(draggedId, g.id);
                              setDraggedGroupId(null);
                            }}
                            className={`flex flex-col gap-2 sm:flex-row sm:items-center py-2.5 px-3 rounded-xl border border-gray-200/80 dark:border-gray-700/80 transition-opacity ${
                              editingGroupId !== g.id ? 'cursor-grab active:cursor-grabbing' : ''
                            } ${draggedGroupId === g.id ? 'opacity-50 bg-teal-50/50 dark:bg-teal-900/10' : 'bg-white dark:bg-gray-800/50'}`}
                          >
                            {editingGroupId === g.id ? (
                              <>
                                <TextInput
                                  value={editingGroupTitle}
                                  onChange={(e) => setEditingGroupTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRenameGroup(g.id, editingGroupTitle);
                                    if (e.key === 'Escape') setEditingGroupId(null);
                                  }}
                                  className="flex-1 min-w-0"
                                  showClearButton={false}
                                />
                                <div className="flex gap-1.5">
                                  <Button type="button" size="icon" onClick={() => handleRenameGroup(g.id, editingGroupTitle)} title="Save" aria-label="Save">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  </Button>
                                  <Button type="button" variant="outline" size="icon" onClick={() => setEditingGroupId(null)} title="Cancel" aria-label="Cancel">
                                    <XIcon className="w-5 h-5" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="flex items-center gap-2 flex-shrink-0 text-gray-400 dark:text-gray-500 pointer-events-none" title="Drag to reorder" aria-hidden>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" /></svg>
                                </span>
                                <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">{g.title}</span>
                                <div className="flex gap-1.5 flex-shrink-0">
                                  <Button type="button" variant="ghost" size="icon" onClick={() => { setEditingGroupId(g.id); setEditingGroupTitle(g.title); }} title="Rename">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveGroup(g.id)} title="Remove" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </Button>
                                </div>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>

                {/* Add widget + Current widgets side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-stretch md:min-w-0">
                  {/* Add widget card */}
                  <Card className="rounded-2xl overflow-hidden flex flex-col min-h-0 min-w-0 md:min-w-[260px]">
                    <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80 flex-shrink-0 space-y-2">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add widget</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {availableToAdd.length} available to add ({availableDefinitions.length} total). Widget types already on the dashboard are not listed here.
                        </p>
                      </div>
                      {availableToAdd.length > 0 && categoriesInAvailable.length > 1 && (
                        <div>
                          <Label>Filter by module</Label>
                          <Select
                            value={addWidgetCategory}
                            onChange={(e) => setAddWidgetCategory((e.target.value || '') as WidgetCategory | '')}
                            options={[
                              { value: '', label: `All (${availableToAdd.length})` },
                              ...categoriesInAvailable.map((cat) => ({
                                value: cat,
                                label: `${categoryLabels[cat] ?? cat} (${availableToAdd.filter((d) => d.category === cat).length})`,
                              })),
                            ]}
                            showClearButton={false}
                            className="mt-1"
                          />
                        </div>
                      )}
                    </div>
                    <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-y-auto">
                      {availableToAdd.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">All available widgets are already on the dashboard.</p>
                      ) : availableToAddFiltered.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No widgets in this module. Try another filter or add from &quot;All&quot;.</p>
                      ) : (
                        <div className="space-y-2">
                          {availableToAddFiltered.map((d) => (
                            <Button
                              key={d.id}
                              type="button"
                              variant="outline"
                              onClick={() => handleAddWidget(d.id)}
                              title={d.description}
                              className="w-full justify-start text-left h-auto py-3 px-4 rounded-xl group"
                            >
                              <span className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400">{d.name}</span>
                                {d.dashboardFilterSupport?.timeRange && (
                                  <span className="shrink-0 p-1 rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" title="Uses Time range">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                  </span>
                                )}
                                {d.dashboardFilterSupport?.scope && (
                                  <span className="shrink-0 p-1 rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" title="Uses Scope (My work / Team)">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                  </span>
                                )}
                              </span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{d.description}</span>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Current widgets card */}
                  <Card className="rounded-2xl overflow-hidden flex flex-col min-h-0 min-w-0 md:min-w-[260px]">
                    <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Current widgets</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{widgets.length} on this dashboard · Drag to reorder</p>
                    </div>
                    <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-y-auto">
                      {sortedWidgets.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No widgets yet. Add one from the list.</p>
                      ) : (
                        <ul className="space-y-2">
                          {sortedWidgets.map((w) => {
                            const def = getWidgetDefinition(w.widgetType);
                            return (
                              <li
                                key={w.id}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('text/plain', w.id);
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDraggedWidgetId(w.id);
                                }}
                                onDragEnd={() => setDraggedWidgetId(null)}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const draggedId = e.dataTransfer.getData('text/plain');
                                  if (draggedId && draggedId !== w.id) handleReorderWidgets(draggedId, w.id);
                                  setDraggedWidgetId(null);
                                }}
                                className={`flex flex-col gap-2 sm:flex-row sm:items-center justify-between py-3 px-4 rounded-xl bg-white dark:bg-gray-800/50 border border-gray-200/80 dark:border-gray-700/80 gap-2 cursor-grab active:cursor-grabbing ${draggedWidgetId === w.id ? 'opacity-50' : ''}`}
                              >
                                <span className="flex items-center gap-2 flex-shrink-0 text-gray-400 dark:text-gray-500 pointer-events-none" title="Drag to reorder" aria-hidden>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" /></svg>
                                </span>
                                <div className="flex-1 min-w-0 sm:min-w-[120px] flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white break-words line-clamp-2" title={def?.name ?? w.widgetType}>{def?.name ?? w.widgetType}</span>
                                  {def?.dashboardFilterSupport?.timeRange && (
                                    <span className="shrink-0 p-1 rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" title="Uses Time range">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </span>
                                  )}
                                  {def?.dashboardFilterSupport?.scope && (
                                    <span className="shrink-0 p-1 rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" title="Uses Scope (My work / Team)">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                                  {groups.length > 0 && (
                                    <Select
                                      value={w.groupId ?? ''}
                                      onChange={(e) => handleSetWidgetGroup(w.id, (e.target.value as string) || undefined)}
                                      options={[{ value: '', label: 'Other' }, ...groups.map((g) => ({ value: g.id, label: g.title }))]}
                                      showClearButton={false}
                                      className="min-w-[100px] text-sm"
                                    />
                                  )}
                                  <div className="flex gap-1">
                                    <Button type="button" variant="ghost" size="icon" onClick={() => handleOpenConfig(w)} title="Configure">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </Button>
                                    <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveWidget(w.id)} title="Remove" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </Button>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </Card>
                </div>

                {layouts.length > 1 && (
                  <Card className="overflow-hidden rounded-2xl">
                    <div className="px-4 sm:px-5 py-3 border-b border-gray-200/80 dark:border-gray-700/80">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Saved layouts</h3>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1.5">
                        {layouts.map((l) => (
                          <li key={l.id} className="flex items-center gap-2">
                            <span>{l.name}</span>
                            {l.isDefault && <Badge variant="accent">default</Badge>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete dashboard"
        message={deleteConfirm ? `Delete dashboard "${deleteConfirm.name}"? This cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default DashboardSettings;
